// Composable for making API calls from Gena (no authentication)
export function useApiFetch() {
  // When SWA is linked to a backend, /api/* is proxied automatically.
  // Only rewrite to absolute base when VITE_API_BASE is explicitly set.
  const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE as string | undefined

  async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Respect SWA backend linking: if no API_BASE_URL is provided, use relative /api/*
    let targetUrl = url
    if (API_BASE_URL) {
      if (url.startsWith('/api/')) {
        targetUrl = `${API_BASE_URL}${url}`
      } else if (!url.startsWith('http')) {
        targetUrl = `${API_BASE_URL}/api/${url}`
      }
    } else {
      // No base configured; ensure non-http URLs are under /api when needed
      if (!url.startsWith('http') && !url.startsWith('/')) {
        targetUrl = `/api/${url}`
      }
    }

    // Set default headers
    const headers = new Headers(options.headers)
    const method = (options.method || 'GET').toUpperCase()
    const hasBody = options.body !== undefined && options.body !== null
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
    if (
      !headers.has('Content-Type') &&
      hasBody &&
      !isFormData &&
      method !== 'GET' &&
      method !== 'HEAD'
    ) {
      headers.set('Content-Type', 'application/json')
    }
    if (isFormData) headers.delete('Content-Type')

    // Make the request without authentication
    return fetch(targetUrl, {
      ...options,
      headers,
      credentials: 'include', // Include credentials for CORS
    })
  }

  async function getJson<T = any>(url: string): Promise<T> {
    const response = await apiFetch(url)
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  async function postJson<T = any>(url: string, data: any): Promise<T> {
    const response = await apiFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  return {
    apiFetch,
    getJson,
    postJson,
    API_BASE_URL,
  }
}

export default useApiFetch
